import { useEffect, useMemo, useState } from 'react'
import { useAppDispatch, useAppSelector } from 'redux/toolkit/hooks'
import { getEmailServerSettings } from 'redux/features/emailServer/emailServerSlice'
import { isFailed, isPending, isSuccess } from 'redux/toolkit/api'
import useUserRights from 'components/libs/userRights/useUserRights'
import { gotoAdminLandingPage } from 'lib/routesConfig'
import { EGD_PRODUCT_ID, getPamProductData, IP_PRODUCT_ID, IR_PRODUCT_ID } from 'redux/features/pam/pamSlice'
import { getIpirAccount } from 'redux/features/ipir/ipirSlice'
import { InitialSetupFlowSettings } from '../setupFlow/types/egdWizardTypes'

export interface State {
  loading: boolean
  failed: boolean
  newWizard?: boolean
  wizardInitialSettings: InitialSetupFlowSettings
}

export type WizardSelectorLogic = [State]

export const useWizardSelectorLogic = (): WizardSelectorLogic => {
  const dispatch = useAppDispatch()
  const {
    isCplAccount,
    isGetEmailServerSettingsIsSuccess,
    isProductDataSuccess,
    isGetEmailServerSettingsInProgress,
    isGetEmailServerSettingsFailed,
    emailServerSettings,
    productData,
    ipirAccountInProgress,
    ipirAccountSuccess,
    ipirAccountFailed,
    ipirAccount
  } = useAppSelector(_store => ({
    isCplAccount: !!_store.auth.accessTokenObject?.isCplAccount,
    isGetEmailServerSettingsIsSuccess: isSuccess(_store.emailServer.getEmailServerSettingsApiStatus),
    isProductDataSuccess: isSuccess(_store.pam.api.getProductData),
    isGetEmailServerSettingsInProgress: isPending(_store.emailServer.getEmailServerSettingsApiStatus),
    isGetEmailServerSettingsFailed: isFailed(_store.emailServer.getEmailServerSettingsApiStatus),
    emailServerSettings: _store.emailServer.emailServerSettings,
    productData: _store.pam.productData,
    ipirAccountInProgress: isPending(_store.ipir.api.getIpirAccount),
    ipirAccountSuccess: isSuccess(_store.ipir.api.getIpirAccount),
    ipirAccountFailed: isFailed(_store.ipir.api.getIpirAccount),
    ipirAccount: _store.ipir.account
  }))
  const { userFeatures } = useUserRights()
  const [newWizard, setNewWizard] = useState<boolean | undefined>(undefined)
  const [wizardInitialSettings, setWizardInitialSettings] = useState<InitialSetupFlowSettings>({
    ipirSection: true,
    egdSection: true
  })

  useEffect(() => {
    // CPL-only users never enter the setup flow or the old wizard: forward them before any fetch or wizard choice
    if (isCplAccount) {
      gotoAdminLandingPage()
      return
    }
    dispatch(getEmailServerSettings())
    dispatch(getPamProductData())
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    // If we have tokens that are not ETS that means the account is setup for IP/IR so we display egd sections only
    if (ipirAccountSuccess) {
      if (ipirAccount?.accessTokens.filter(token => !token.products.includes('ets')).length) {
        setWizardInitialSettings({ ...wizardInitialSettings, ipirSection: false })
      }
    }
    // If IPIR fails for some reason don't block the flow but show EGD section only
    if (ipirAccountFailed) {
      setWizardInitialSettings({ ...wizardInitialSettings, ipirSection: false })
    }
    // We don't need to retrigger on wizardInitialSettings change
    // eslint-disable-next-line
  }, [ipirAccountSuccess, ipirAccount, ipirAccountFailed])

  useEffect(() => {
    if (isGetEmailServerSettingsIsSuccess && isProductDataSuccess) {
      // Enter admin if wizard is completed
      if (emailServerSettings?.wizardCompleted) {
        gotoAdminLandingPage()
        setNewWizard(undefined)
        return
      }

      // If we have setup flow section that means setup flow has been started with corresponding section no further checks are needed
      if (emailServerSettings?.setupFlowSections) {
        setNewWizard(true)
        setWizardInitialSettings(emailServerSettings?.setupFlowSections)
        return
      }

      let hasIp = false
      let hasIr = false
      let hasEgd = false
      productData?.data.subscriptionGroups.forEach(group => {
        group?.subscriptions.forEach(subscription => {
          if (subscription.product.id === EGD_PRODUCT_ID && subscription.status === 'ACTIVE') {
            hasEgd = true
          }
          if (subscription.product.id === IP_PRODUCT_ID && subscription.status === 'ACTIVE') {
            hasIp = true
          }
          if (subscription.product.id === IR_PRODUCT_ID && subscription.status === 'ACTIVE') {
            hasIr = true
          }
        })
      })

      if (userFeatures?.areWizardsDeprecated) {
        setNewWizard(true)

        // If they have only EGD show EGD sections only else we need ipir account info
        if (!hasIp && !hasIr && hasEgd) {
          setWizardInitialSettings({ ...wizardInitialSettings, ipirSection: false })
        } else {
          dispatch(getIpirAccount())
        }
      } else {
        // If there are settings (first step in old wizard is region) and no version means the wizard was already started and should be v1
        if (emailServerSettings?.region && !emailServerSettings.version) {
          setNewWizard(false)
          return
        }

        // If there is version 'v1' we should always end up on v1 no matter the split flag
        if (emailServerSettings?.version === 'v1') {
          setNewWizard(false)
          return
        }

        // If there is version 'v2' we should always end up on v2 no matter the split flag
        if (emailServerSettings?.version === 'v2') {
          setNewWizard(true)
          if (emailServerSettings?.setupFlowSections) {
            setWizardInitialSettings(emailServerSettings?.setupFlowSections)
          }
          return
        }

        if (userFeatures?.convergedSetupFlowRollout) {
          // If they have all three products we need to check ipir tokens
          if ((hasIp || hasIr) && hasEgd) {
            setNewWizard(true)
            dispatch(getIpirAccount())
            return
          }

          // If they have only EGD show EGD sections only
          if (!hasIp && !hasIr && hasEgd) {
            setNewWizard(true)
            setWizardInitialSettings({ ...wizardInitialSettings, ipirSection: false })
            return
          }
        }

        setNewWizard(!!userFeatures?.convergedSetupFlowRollout)
      }
    }
    // We don't need to retrigger on wizardInitialSettings change
    // eslint-disable-next-line
  }, [
    dispatch,
    isGetEmailServerSettingsIsSuccess,
    isProductDataSuccess,
    emailServerSettings,
    productData,
    userFeatures
  ])

  return useMemo(
    () => [
      {
        newWizard,
        wizardInitialSettings,
        loading:
          (isGetEmailServerSettingsInProgress || ipirAccountInProgress || newWizard === undefined) &&
          !isGetEmailServerSettingsFailed,
        failed: isGetEmailServerSettingsFailed
      }
    ],
    [
      newWizard,
      wizardInitialSettings,
      isGetEmailServerSettingsInProgress,
      ipirAccountInProgress,
      isGetEmailServerSettingsFailed
    ]
  )
}
